import { fmt2 } from "./common_funcs.js";

export class Vector2
{
  public x: number;
  public y: number;

  constructor( x: number, y: number )
  {
    this.x = x;
    this.y = y;
  }

  set( x: number, y: number )
  {
    this.x = x;
    this.y = y;
  }

  copyFrom( v: Vector2 )
  {
    this.x = v.x;
    this.y = v.y;
  }

  equals( v: Vector2 ): boolean
  {
    return this.x === v.x && this.y === v.y;
  }

  isZeroVector(): boolean
  {
    return this.x === 0 && this.y === 0;
  }

  // Метод для сложения двух векторов (аналог оператора "+")
  add( other: Vector2 ): Vector2
  {
    return new Vector2( this.x + other.x, this.y + other.y );
  }

  // Метод для вычитания двух векторов (аналог оператора "-")
  subtract( other: Vector2 ): Vector2
  {
    return new Vector2( this.x - other.x, this.y - other.y );
  }

  // Метод для умножения вектора на скаляр (аналог оператора "*")
  multiply( scalar: number ): Vector2
  {
    return new Vector2( this.x * scalar, this.y * scalar );
  }

  divide( scalar: number ): Vector2
  {
    if ( scalar == 0 )
      return new Vector2( 0, 0 );
    return new Vector2( this.x / scalar, this.y / scalar );
  }

  // Скалярное произведение двух векторов
  dot( other: Vector2 ): number
  {
    return this.x * other.x + this.y * other.y;
  }

  addThis( other: Vector2 )
  {
    this.x += other.x;
    this.y += other.y;
  }

  subtractThis( other: Vector2 )
  {
    this.x -= other.x;
    this.y -= other.y;
  }

  multiplyThis( scalar: number )
  {
    this.x *= scalar;
    this.y *= scalar;
  }

  multiplyThis_elementByElement( v: Vector2 )
  {
    this.x *= v.x;
    this.y *= v.y;
  }

  divideThis( scalar: number )
  {
    if ( scalar == 0 )
    {
      this.set( 0, 0 );
      return;
    }

    this.x /= scalar;
    this.y /= scalar;
  }

  // Скалярное произведение двух векторов
  dotThis( other: Vector2 )
  {
    this.x *= other.x;
    this.y *= other.y;
  }

  roundToInt()
  {
    this.x = Math.round( this.x );
    this.y = Math.round( this.y );
  }

  // Вычисление длины (модуля) вектора
  length(): number
  {
    return Math.sqrt( this.x * this.x + this.y * this.y );
  }

  // Нормализация вектора (приведение его к единичной длине)
  normalize(): Vector2
  {
    const len = this.length();
    if ( len === 0 )
    {
      throw new Error( "Cannot normalize a zero-length vector." );
    }
    return new Vector2( this.x / len, this.y / len );
  }

  // Вычисление расстояния до другого вектора
  distanceTo( other: Vector2 ): number
  {
    return this.subtract( other ).length();
  }

  rectangularDistanceTo( other: Vector2 ): number
  {
    return Math.abs( this.x - other.x )
      + Math.abs( this.y - other.y );
  }

  // Поворот вектора на заданный угол (в радианах)
  rotate( angle: number ): Vector2
  {
    const cos = Math.cos( angle );
    const sin = Math.sin( angle );
    return new Vector2( this.x * cos - this.y * sin, this.x * sin + this.y * cos );
  }

  // Создание копии вектора
  clone(): Vector2
  {
    return new Vector2( this.x, this.y );
  }

  // Преобразование вектора в строку для удобного вывода
  toString(): string
  {
    return `(${fmt2(this.x)}, ${fmt2(this.y)})`;
  }

  static randomInCircle( radius: number ): Vector2
  {
    const angle            = Math.random() * Math.PI * 2;
    const sum_two_uniforms = Math.random() + Math.random();
    const radius_ratio     = sum_two_uniforms > 1 ? 2 - sum_two_uniforms : sum_two_uniforms;
    const distance         = radius_ratio * radius;

    return new Vector2( Math.cos( angle ) * distance, Math.sin( angle ) * distance );
  }


  // Линейная интерполяция между векторами a и b.
  // @param a — начальный вектор
  // @param b — конечный вектор
  // @param t — коэффициент [0..1], обычно это уже «eased» значение из applyEasing
  
  static lerp( a: Vector2, b: Vector2, t: number ): Vector2
  {
    return new Vector2(
      a.x + ( b.x - a.x ) * t,
      a.y + ( b.y - a.y ) * t
    );
  }
}
